import React, { useEffect, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { siteData } from '../data';
import { CheckCircle2, Shield, Users, Clock } from 'lucide-react';

const Counter = ({ target, duration = 2000 }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    let startTimestamp = null;
    const step = (timestamp) => {
      if (!startTimestamp) startTimestamp = timestamp;
      const progress = Math.min((timestamp - startTimestamp) / duration, 1);
      setCount(Math.floor(progress * target));
      if (progress < 1) {
        window.requestAnimationFrame(step);
      }
    };
    window.requestAnimationFrame(step);
  }, [target, duration]);

  return <span>{count}</span>;
};

export default function AboutPage() {
  // Scroll to top when page mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const values = [
    {
      icon: <Shield className="w-8 h-8 text-black" />,
      title: "Kvalitetsgaranti",
      description: "Vi er ikke tilfredse, før du er det. Hvis der mod forventning skulle være en stribe, kommer vi og retter det med det samme."
    },
    {
      icon: <Users className="w-8 h-8 text-black" />,
      title: "Personlig Service",
      description: "Du møder altid den samme glade vinduespudser, der kender dine præferencer og dit hjem."
    },
    {
      icon: <Clock className="w-8 h-8 text-black" />,
      title: "Punktlighed",
      description: "Vi ved, at din tid er vigtig. Derfor overholder vi vores aftaler og giver besked, hvis vi bliver forsinket."
    }
  ];

  return (
    <div className="pt-20">
      <Helmet>
        <title>Om VHservice | Din lokale vinduespudser</title>
        <meta name="description" content="Læs mere om VHservice, vores historie og vores værdier. Vi går op i kvalitet, punktlighed og personlig service." />
      </Helmet>
      {/* Hero Section */}
      <section className="bg-blue-900 py-24 relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <img 
            src="https://assets.auxodev.dk/assets/a63bb5b8-c11e-4d14-812c-e833a8e6def1/5c07a12d-5d57-4360-aac6-16ff478c049c.jpg" 
            alt="Baggrund" 
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6">Om VHservice</h1>
          <p className="text-xl text-blue-100 max-w-2xl mx-auto leading-relaxed">
            Mere end bare vinduespudsning – vi bringer lyset og udsigten tilbage i dit liv med professionel stolthed og et smil.
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">
            <div className="space-y-8">
              <h2 className="text-3xl font-bold text-gray-900 border-l-4 border-black pl-6">Vores Historie</h2>
              <div className="text-lg text-gray-600 space-y-6 leading-relaxed">
                <p>
                  VHservice blev grundlagt med en vision om at professionalisere vinduespudsning i lokalområdet. Det, der startede som en enkelt mands passion for rene ruder, er i dag vokset til en respekteret servicevirksomhed med mange trofaste kunder.
                </p>
                <p>
                  Vi har gennem årene investeret i det nyeste udstyr, der sikrer et stribefrit resultat uden brug af skrappe kemikalier.
                </p>
                <p>
                  Vores fundament er bygget på tillid. Vi ved, at det er en tillidssag at lukke folk ind i sit private hjem, og vi værner om den tillid hver eneste dag.
                </p>
              </div>
            </div>
            <div className="relative">
              <img 
                src="https://assets.auxodev.dk/assets/a63bb5b8-c11e-4d14-812c-e833a8e6def1/5c07a12d-5d57-4360-aac6-16ff478c049c.jpg" 
                alt="VHservice Teamet" 
                className="rounded-none shadow-2xl w-full"
              />
              <div className="absolute -bottom-6 -right-6 bg-white p-8 text-black shadow-xl hidden md:block border border-gray-100">
                <p className="text-4xl font-bold"><Counter target={10} />+</p>
                <p className="text-sm uppercase tracking-wider font-semibold">Års erfaring</p>
              </div>
            </div>
          </div>

          {/* Values Grid */}
          <div className="grid md:grid-cols-3 gap-12">
            {values.map((value, index) => (
              <div key={index} className="bg-white p-10 rounded-none shadow-lg hover:shadow-xl transition-shadow border-t-4 border-black">
                <div className="mb-6">{value.icon}</div>
                <h3 className="text-xl font-bold text-gray-900 mb-4">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <h2 className="text-3xl font-bold mb-6">Hvorfor vælge VHservice?</h2>
            <p className="text-gray-600 text-lg">Vi gør en dyd ud af detaljen og sikrer, at dine vinduer står krystalklare hver gang.</p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="flex items-start space-x-4 bg-white p-6 shadow-sm">
              <CheckCircle2 className="text-black flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Kvalitetsudstyr</h4>
                <p className="text-gray-600">Vi bruger professionelt udstyr og miljøvenlige midler, hvilket giver et stribefrit resultat og holder vinduerne rene længere.</p>
              </div>
            </div>
            <div className="flex items-start space-x-4 bg-white p-6 shadow-sm">
              <CheckCircle2 className="text-black flex-shrink-0 mt-1" />
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Aftørring af karme</h4>
                <p className="text-gray-600">Vi tørrer altid rammer og karme af, så hele vinduespartiet ser ud som nyt.</p>
              </div>
            </div>


          </div>
        </div>
      </section>
    </div>
  );
}
